import { useState } from 'react'
import type { IntakeDomain } from '../../orders/api/intakeApi'
import { useAuthStore } from '../../auth/store'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs'
import { MerchantFieldConfig } from './MerchantFieldConfig'
import { SolutionCatalogEditor } from './SolutionCatalogEditor'

interface FieldBuilderProps {
  merchantId: string
  solutionId: string
  domain: IntakeDomain
}

/**
 * The Field Config experience for one (merchant, domain), template-copy model:
 *   Tab 1 "This merchant" — read-only template preview when unconfigured, or the full
 *     editor over the merchant's own field list (everyone who can reach the page).
 *   Tab 2 "Solution catalog" — the solution TEMPLATE editor, gated to COLLECTIVE/TCS admins.
 *
 * The catalog tab is hidden (not just disabled) for non-COLLECTIVE users, matching how
 * other COLLECTIVE-only Osprey surfaces gate via useAuthStore().user.roleScope.
 */
export function FieldBuilder({ merchantId, solutionId, domain }: FieldBuilderProps) {
  const user = useAuthStore((s) => s.user)
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE'
  const [tab, setTab] = useState<'merchant' | 'catalog'>('merchant')

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as 'merchant' | 'catalog')}>
      <TabsList className="mb-4">
        <TabsTrigger value="merchant">This merchant</TabsTrigger>
        {isCollectiveStaff && <TabsTrigger value="catalog">Solution catalog</TabsTrigger>}
      </TabsList>

      <TabsContent value="merchant">
        <MerchantFieldConfig merchantId={merchantId} domain={domain} />
      </TabsContent>

      {isCollectiveStaff && (
        <TabsContent value="catalog">
          <SolutionCatalogEditor solutionId={solutionId} domain={domain} />
        </TabsContent>
      )}
    </Tabs>
  )
}
